import styled from "@emotion/styled";
import { Dialog, Typography } from "@mui/material";

export const StyledTypography = styled(Typography)`
  color: var(--Text-text-primary, #002035);
  font-family: var(--Typography-font-family-Roobert, Roobert);
  font-size: var(--Typography-font-sizes-sm-16, 16px);
  font-style: normal;
  font-weight: var(--Typography-font-weight-400, 400);
  line-height: var(--Typography-line-height-sm, 20px); /* 125% */
`;

export const StyledActiveLabel = styled(Typography)`
  color: var(--Text-text-brand-salmon, #fc4c4c);
  font-family: var(--Typography-font-family-Roobert, Roobert);
  font-size: var(--Typography-font-sizes-sm-16, 16px);
  font-style: normal;
  font-weight: var(--Typography-font-weight-600, 600);
  line-height: var(--Typography-line-height-sm, 20px); /* 125% */
`;

export const StyledSecondaryLabel = styled(Typography)`
  color: var(--Text-text-secondary, #5c6469);
  font-family: var(--Typography-font-family-Roobert, Roobert);
  font-size: var(--Typography-font-sizes-sm-14, 14px);
  font-style: normal;
  font-weight: var(--Typography-font-weight-400, 400);
  line-height: var(--Typography-line-height-xs, 16px); /* 114.286% */
`;

export const StyledSubmissionWarning = styled.div`
  display: flex;
  padding: var(--Spacing-spacing-sm, 12px);
  flex-direction: column;
  align-items: flex-start;
  gap: var(--Spacing-spacing-sm, 12px);
  border-radius: var(--Radius-radius-minimal-2, 10px);
  background: var(--Surface-surface-light-green, #f0fef9);
  align-self: stretch;
`;

export const StyledWarningTypography = styled(Typography)`
  color: var(--navy-blue-900-p, var(--navy-blue-900-p, #002035));

  /* Paragraph/Roobert 14px/Regular */
  font-family: var(--Typography-font-family-Roobert, Roobert);
  font-size: var(--Typography-font-sizes-sm-14, 14px);
  font-style: normal;
  font-weight: var(--Typography-font-weight-400, 400);
  line-height: var(--Typography-line-height-xs, 16px); /* 114.286% */
`;

interface DialogWrapperProps {
  maxWidth?: boolean;
}
export const DialogWrapper = styled(Dialog)<DialogWrapperProps>(({ maxWidth }) => ({
  display: "flex",
  width: "100%", // Keep this to ensure background overlay covers the entire screen
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "10px",
  cursor: "default",
  background: "rgba(0, 32, 53, 0.7)",

  // this is repeated so that mobile scrollbar is styled too
  "& .MuiDialog-paper": {
    /* Style scrollbar for WebKit (Chrome, Safari) */
    "&::-webkit-scrollbar": {
      width: "10px" /* Width of the scrollbar */,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#E5E7E8" /* Color of the scrollbar thumb */,
      borderRadius: "5px" /* Border radius of the scrollbar thumb */,
    },

    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent" /* Color of the scrollbar thumb */,
    },
  },

  // needed to ensure when onfido SDK is open on mobile, the modal stays full width on the screen
  "& .MuiDialog-container": {
    width: "100%",
  },

  // control width of modal for desktop
  ...(maxWidth === false && {
    "& .MuiDialog-paper": {
      width: "500px", // as per design

      /* Style scrollbar for WebKit (Chrome, Safari) */
      "&::-webkit-scrollbar": {
        width: "10px" /* Width of the scrollbar */,
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#E5E7E8" /* Color of the scrollbar thumb */,
        borderRadius: "5px" /* Border radius of the scrollbar thumb */,
      },

      "&::-webkit-scrollbar-track": {
        backgroundColor: "transparent" /* Color of the scrollbar thumb */,
      },
    },
  }),
}));

export const HeaderWrapper = styled("div")(() => ({
  display: "flex",
  padding: "16px 20px",
  justifyContent: "center",
  alignItems: "center",
  gap: "4px",
  alignSelf: "stretch",
  background: "#002035",
}));

export const HeaderWrapperMobile = styled("div")(() => ({
  display: "flex",
  height: "110px",
  padding: "24px 16px",
  justifyContent: "space-between",
  alignItems: "center",
  flexShrink: 0,
  background: "#002035",
  boxShadow: "0px 4px 20px 0px rgba(173, 192, 203, 0.30)",
}));

export const HeaderTitle = styled("div")(() => ({
  flex: "1 0 0",
  color: "#FFF",
  textAlign: "center",
  fontFamily: "Roobert",
  fontSize: "23px",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "28px",
  letterSpacing: "-0.46px",
}));

export const HeaderTitleMobile = styled("div")(() => ({
  flex: "1 0 0",
  color: "#FFF",
  textAlign: "center",
  fontFamily: "Roobert",
  fontSize: "23px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "28px",
  letterSpacing: "-0.46px",
}));

export const HeaderIcon = styled("div")(() => ({
  display: "flex",
  width: "16px",
  height: "16px",
  justifyContent: "center",
  alignItems: "center",
  cursor: "pointer",
}));

export const HeaderIconMobile = styled("div")(() => ({
  display: "flex",
  width: "24px",
  height: "24px",
  justifyContent: "center",
  alignItems: "center",
  cursor: "pointer",
}));

export const ContentWrapper = styled("div")(() => ({
  display: "flex",
  padding: "24px 24px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "24px",
  alignSelf: "stretch",
}));

export const ContentWrapperMobile = styled("div")(() => ({
  display: "flex",
  padding: "0px 16px",
  flexDirection: "column",
  alignItems: "flex-start",
  flex: "1 0 0",
  alignSelf: "stretch",
}));

export const ContentHeadingWrapperMobile = styled("div")(() => ({
  display: "flex",
  padding: "24px 0px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "8px",
  alignSelf: "stretch",
}));

export const ContentHeadingTitleMobile = styled("div")(() => ({
  alignSelf: "stretch",
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "28px",
  fontStyle: "normal",
  fontWeight: "600",
  lineHeight: "34px",
  letterSpacing: "-0.56px",
}));

export const ContentHeadingTypography = styled("div")(() => ({
  alignSelf: "stretch",
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "20px",
}));

export const SubContentWrapperMobile = styled("div")(() => ({
  display: "flex",
  paddingBottom: "28px",
  flexDirection: "column",
  alignItems: "center",
  gap: "24px",
  flex: "1 0 0",
  alignSelf: "stretch",
}));

export const ButtonText = styled("div")(() => ({
  color: "#FFF",
  fontFamily: "Roobert",
  fontSize: "18px",
  fontStyle: "normal",
  fontWeight: "600",
  lineHeight: "22px",
}));

export const LimitationsContentWrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "16px",
  alignSelf: "stretch",
}));

export const PushButtonToBottom = styled("div")(() => ({
  flex: "1 0 0",
}));

export const LimitationsLineWrapper = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  alignSelf: "stretch",
}));

export const LimitationsLineTypography = styled("div")(() => ({
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "16px",
}));

export const FormInputWrapper = styled("div")<{ isActive: boolean }>(({ isActive }) => ({
  display: "flex",
  width: "100%",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "12px",
  opacity: isActive ? 1 : 0.2,
  pointerEvents: isActive ? "auto" : "none",
}));

export const FormInputHeading = styled("div")<{ includeFlex?: boolean }>(
  ({ includeFlex = true }) => ({
    flex: includeFlex ? "1 0 0" : "none",
    color: "#FC4C4C",
    fontFamily: "Roobert",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "20px",
  }),
);

export const FormInputSubHeading = styled("div")(() => ({
  flex: "1 0 0",
  color: "#5C6469",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "16px",
}));

export const FormInputFieldWrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "8px",
  alignSelf: "stretch",
}));

interface FormInputFieldBoxProps {
  hasError: boolean;
  isActive: boolean;
}
export const FormInputFieldBox = styled("div")<FormInputFieldBoxProps>(
  ({ hasError, isActive }) => ({
    display: "flex",
    padding: "12px",
    alignItems: "center",
    gap: "8px",
    alignSelf: "stretch",
    borderRadius: "8px",
    border: `1px solid ${hasError ? "#EF4444" : isActive ? "#002035" : "#E5E7E8"}`,
    cursor: "text",
  }),
);

export const FormInputFieldText = styled("input")(() => ({
  flex: "1 0 0",
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "16px",
  border: "none",
  outline: "none",
  background: "transparent",

  "&::placeholder": {
    color: "#BDC2C5",
    userSelect: "none",
  },

  "&:disabled": {
    background: "transparent",
  },
}));

interface FormInputFieldIconProps {
  hasOnClick?: boolean;
}
export const FormInputFieldIcon = styled("div")<FormInputFieldIconProps>(({ hasOnClick }) => ({
  display: "flex",
  width: "12px",
  height: "12px",
  padding: "1.5px 0px 1.269px 0px",
  justifyContent: "center",
  alignItems: "center",
  ...(hasOnClick && { cursor: "pointer" }),
}));

export const FormInputErrorWrapper = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  alignSelf: "stretch",
}));

export const FormInputErrorText = styled("div")(() => ({
  flex: "1 0 0",
  color: "#EF4444",
  fontFamily: "Söhne",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "14px",
}));

export const FormInputErrorLinkText = styled("button")(() => ({
  flex: "1 0 0",
  color: "#EF4444",
  fontFamily: "Roobert",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "14px",
  cursor: "pointer",
  textDecorationLine: "underline",
  textDecorationStyle: "solid",
  textDecorationSkipInk: "none",
  textDecorationThickness: "auto",
  textUnderlineOffset: "auto",
  textUnderlinePosition: "from-font",
  textTransform: "capitalize",
  background: "none",
  border: "none",
  padding: 0,
  "&:disabled": {
    cursor: "not-allowed",
    opacity: 0.5,
  },
}));

export const BannerContentWrapper = styled("div")<{ background?: string }>(({ background }) => ({
  display: "flex",
  padding: "12px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "12px",
  alignSelf: "stretch",
  borderRadius: "10px",
  background: background ?? "#F0FEF9",
}));

export const BannerDetails = styled("div")(() => ({
  display: "flex",
  padding: "0px",
  alignItems: "center",
  gap: "12px 12px",
  alignSelf: "stretch",
  flexWrap: "wrap",
}));

export const BannerDetailsText = styled("div")(() => ({
  display: "inline",
  alignItems: "center",
  gap: "12px",
  flex: "1 0 0",
  alignSelf: "stretch",
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "16px",
}));

export const QuitTextWrapper = styled("div")(() => ({
  display: "flex",
  padding: "0px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "8px",
  alignSelf: "stretch",
}));

export const QuitTextLineWrapper = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  alignSelf: "stretch",
}));

export const QuitTextTitle = styled("div")(() => ({
  color: "#002035",
  fontFamily: "Söhne",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "500",
  lineHeight: "16px",
}));

export const QuitTextSubtitle = styled("div")(() => ({
  color: "#5C6469",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "16px",
}));

export const QuitButtonsWrapper = styled("div")<{ isMobile: boolean }>(({ isMobile }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: "28px",
  alignSelf: "stretch",
  flexDirection: isMobile ? "column" : "row",
}));

export const QuitButton = styled("button")(() => ({
  display: "flex",
  width: "100%",
  padding: "12px 16px",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  borderRadius: "4px",
  background: "#F8F9F9",
  color: "#002035",
  fontFamily: "Roobert",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "20px",
  border: "none",

  "&:hover": {
    fontWeight: "600",
    letterSpacing: "-0.32px",
  },
  "&:disabled": {
    cursor: "not-allowed",
    opacity: 0.5,
  },
}));

export const PasswordInputHeadingWrapper = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  alignSelf: "stretch",
});

export const PasswordInputFieldIcon = styled("div")<FormInputFieldIconProps>(({ hasOnClick }) => ({
  display: "flex",
  width: "16px",
  height: "16px",
  padding: "2px 0px 1.692px 0px",
  justifyContent: "center",
  alignItems: "center",
  ...(hasOnClick && { cursor: "pointer" }),
}));

export const InputSubmitWrapper = styled("div")({
  display: "flex",
  padding: "4px 8px",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  borderRadius: "4px",
  cursor: "pointer",
  color: "#4E7086",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "16px",
  textDecorationLine: "underline",
  textDecorationStyle: "solid",
  textDecorationSkipInk: "none",
  textDecorationThickness: "auto",
  textUnderlineOffset: "auto",
  textUnderlinePosition: "from-font",
});
